import { useState, type FormEvent } from "react";
import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/feature/PageHeader";
import Reveal from "@/components/feature/Reveal";

const FORM_URL = "https://readdy.ai/api/form/daf01vrep053ijub1i10";

export default function Contacto() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get("phone_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "phone_alt") return;
      params.append(key, value as string);
    });

    setStatus("loading");
    setFormError("");

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "Ocurrió un error. Inténtalo nuevamente.");
      }
    } catch {
      setStatus("error");
      setFormError("No pudimos enviar tu consulta. Revisa tu conexión e inténtalo nuevamente.");
    }
  }

  return (
    <Layout>
      <PageHeader
        eyebrow="Contacto"
        title="¿Hablamos de tu próximo proyecto?"
        subtitle="Estamos disponibles para resolver tus dudas, entregarte información sobre nuestros proyectos y acompañarte durante el proceso."
      />

      <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Formulario */}
          <Reveal className="lg:col-span-3">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5 rounded-lg border border-background-200/70 bg-background-50 p-7 md:p-9"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label htmlFor="nombre" className="text-sm font-medium text-foreground-700">
                    Nombre
                  </label>
                  <input
                    id="nombre"
                    name="nombre"
                    type="text"
                    required
                    placeholder="Tu nombre"
                    className="rounded-md border border-background-300/60 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="telefono" className="text-sm font-medium text-foreground-700">
                    Teléfono
                  </label>
                  <input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    required
                    placeholder="+56 9 ..."
                    className="rounded-md border border-background-300/60 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-sm font-medium text-foreground-700">
                  E-mail
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="tu@correo.cl"
                  className="rounded-md border border-background-300/60 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="mensaje" className="text-sm font-medium text-foreground-700">
                  Mensaje
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  rows={5}
                  maxLength={500}
                  required
                  placeholder="Cuéntanos qué necesitas..."
                  className="rounded-md border border-background-300/60 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition resize-none"
                />
              </div>

              {/* Honeypot anti-spam */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 text-sm md:text-base font-semibold px-8 py-3.5 transition-colors duration-300 whitespace-nowrap"
              >
                {status === "loading" ? "ENVIANDO..." : "ENVIAR CONSULTA"}
              </button>

              {status === "success" && (
                <p className="text-sm font-medium text-accent-700">
                  ¡Gracias! Hemos recibido tu consulta. Te contactaremos a la brevedad.
                </p>
              )}
              {status === "error" && (
                <p className="text-sm font-medium text-red-600">{formError}</p>
              )}
            </form>
          </Reveal>

          {/* Información de contacto */}
          <Reveal delay={100} className="lg:col-span-2 flex flex-col gap-6">
            <div className="flex flex-col gap-5 rounded-lg border border-background-200/70 bg-background-50 p-7">
              <h3 className="font-heading text-2xl text-foreground-900">Datos de contacto</h3>
              <ul className="flex flex-col gap-4 text-sm text-foreground-600">
                <li>
                  <a href="tel:+56982998322" className="flex items-center gap-3 hover:text-primary-600 transition-colors">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-phone-line text-lg" />
                    </span>
                    +56 9 8299 8322
                  </a>
                </li>
                <li>
                  <a href="mailto:ventas@iadj.cl" className="flex items-center gap-3 hover:text-primary-600 transition-colors">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg" />
                    </span>
                    ventas@iadj.cl
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-map-pin-line text-lg" />
                  </span>
                  Alonso de Córdova 5870, oficina 1013, Las Condes
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-50 p-7">
              <h3 className="font-heading text-2xl text-foreground-900">Dónde encontrarnos</h3>
              <p className="text-sm text-foreground-500">Oficina ADJ · Las Condes, Santiago</p>
              <div className="rounded-md overflow-hidden border border-background-200/70">
                <iframe
                  title="Oficina Inmobiliaria ADJ"
                  src="https://www.google.com/maps?q=Alonso%20de%20C%C3%B3rdova%205870%2C%20Las%20Condes%2C%20Santiago%2C%20Chile&output=embed"
                  className="w-full h-64 border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}